import { atom } from "jotai";
import { User } from "firebase/auth";

import { CURRENT_PLATFORM } from "../../configs/platform.generated";
import { VideoPlayerState } from "./types";

import { AssetResponse } from "@/utils/FetchAssetDetails/types";

/**
 * A Jotai atom that controls the visibility of the Lottie animation.
 *
 * @remarks
 * This atom holds a boolean value:
 * - `true` means the animation should be visible.
 * - `false` means the animation should be hidden.
 *
 * Components using this atom can subscribe to it via `useAtom` to respond to changes in visibility.
 */
const showLottieAtom = atom(true);

const platformAtom = atom<"tv" | "web" | "mobile">(CURRENT_PLATFORM);

/**
 * A Jotai atom that tracks if any modal is currently open.
 *
 * @remarks
 * This atom holds a boolean value:
 * - `true` means a modal is open and body scroll should be prevented.
 * - `false` means no modal is open and body scroll is allowed.
 *
 * Components can use this atom to control body scroll prevention globally.
 */
const isModalOpenAtom = atom(false);

/**
 * A Jotai atom that holds the currently selected asset.
 *
 * @remarks
 * This atom stores the selected AssetResponse object or null:
 * - `AssetResponse` when an asset is selected (e.g., for video playback).
 * - `null` when no asset is selected.
 *
 * Components can use this atom to share selected asset state globally.
 */
const selectedAssetAtom = atom<AssetResponse | null>(null);

/**
 * A Jotai atom that holds all video player related state.
 *
 * @remarks
 * This atom stores video player state including:
 * - `videoRef`: Reference to the HTMLVideoElement for playback control
 * - `isMuted`: Current mute state of the video
 * - `isCastConnected`: Current Chromecast connection state
 * - `resetControlsTimer`: Function to reset the controls auto-hide timer
 * - `currentTime`: Current playback position in seconds (updated by timeupdate events)
 * - `duration`: Total video duration in seconds
 * - `lastSeekTarget`: Tracks the target position of the most recent seek operation
 *                     to filter out stale timeupdate events that were queued before the seek
 * - `isScrubbing`: Whether the user is currently scrubbing the progress bar
 * - `isAdjustingBrightness`: Whether the user is currently adjusting brightness
 * - `isSubtitleOverlayOpen`: Whether the subtitle overlay is currently open
 * - `selectedSubtitle`: Currently selected subtitle track ID
 * - `selectedAudio`: Currently selected audio track ID
 *
 * Components can use this atom to share video player state globally.
 * The progress bar and player hook use this for synchronized time state.
 */
const videoPlayerAtom = atom<VideoPlayerState>({
  videoRef: null,
  isMuted: false,
  isCastConnected: false,
  resetControlsTimer: null,
  currentTime: 0,
  duration: 0,
  lastSeekTarget: null,
  isScrubbing: false,
  isAdjustingBrightness: false,
  isSubtitleOverlayOpen: false,
  selectedSubtitle: "off",
  selectedAudio: "en-5.1",
});

/**
 * A Jotai atom that holds the current authenticated user.
 *
 * @remarks
 * This atom stores the Firebase User object or null:
 * - `User` when a user is authenticated.
 * - `null` when no user is authenticated.
 *
 * Components can use this atom to access current user state globally.
 */
const currentUserAtom = atom<User | null>(null);

/**
 * A Jotai atom that tracks if the initial page is loading.
 *
 * @remarks
 * This atom holds a boolean value:
 * - `true` means the initial page load is in progress and navigation should be hidden.
 * - `false` means the initial page has loaded and navigation can be shown.
 *
 * Components can use this atom to control UI visibility during initial load.
 */
const isInitialPageLoadingAtom = atom(true);

export {
  showLottieAtom,
  platformAtom,
  isModalOpenAtom,
  selectedAssetAtom,
  videoPlayerAtom,
  currentUserAtom,
  isInitialPageLoadingAtom,
};
